/**
 * Copyright © 2025 Safar Glimpse (SafarGlimpse.com). All Rights Reserved.
 * 
 * PROPRIETARY SOFTWARE - Unauthorized copying prohibited.
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://safarglimpse.com' 
  : 'http://localhost:3000';

const SEO = ({
  title,
  description,
  image = '/og-image.jpg',
  type = 'website',
  canonical,
  structuredData,
  noindex = false,
}) => {
  const location = useLocation();
  const url = canonical || `${SITE_URL}${location.pathname}${location.search}`;
  const fullTitle = title
    ? `${title} | SafarGlimpse.com`
    : 'Safar Glimpse - Discover Amazing Places';

  const defaultDescription = 'Explore UNESCO World Heritage Sites and famous places worldwide. Interactive platform with 1,000+ sites, games, and beautiful UI.';

  const metaDescription = description || defaultDescription;
  // Handle image URL - ensure it's a full absolute URL
  const imageUrl = image
    ? (image.startsWith('http') ? image : `${SITE_URL}${image}`)
    : `${SITE_URL}/og-image.jpg`; // Default fallback image - optimized 1200x630 JPEG

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="copyright" content="Copyright © 2025 Safar Glimpse (SafarGlimpse.com). All Rights Reserved." />
      
      {/* Google Search Console Verification */}
      <meta name="google-site-verification" content="0e_0je2Yvw1jBZGhLDvE2B73-yK8BbWumVS8qq8v0qI" />
      
      {/* Microsoft / Bing Webmaster Tools Verification */}
      <meta name="msvalidate.01" content="CB8F8DA7D6A2965EEC692D98DDF8719A" />
      
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Canonical URL */}
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content="SafarGlimpse.com" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={fullTitle} />
      <meta name="twitter:site" content="@SafarGlimpse" />
      
      {/* Additional image meta tags for better Google indexing */}
      <meta name="image" content={imageUrl} />

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        Array.isArray(structuredData) 
          ? structuredData.map((schema, index) => (
              <script key={index} type="application/ld+json">
                {JSON.stringify(schema)}
              </script>
            ))
          : (
            <script type="application/ld+json">
              {JSON.stringify(structuredData)}
            </script>
          )
      )}
    </Helmet>
  );
};

export default SEO;
